import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    dataLayer: any[];
    DateTime: any;
    gtag: (...args: any[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private ready = false;
  private queue: Array<() => void> = [];

  init() {
    if (!environment.googleAnalyticsToken) return; // não carrega GA se não houver ID

    // 1) carrega script
    const s1 = document.createElement('script');
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsToken}`;
    document.head.appendChild(s1);

    // 2) configura gtag + desliga page_view automático (SPA)
    const s2 = document.createElement('script');
    s2.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){ dataLayer.push(arguments); }
      gtag('js', new Date());
      gtag('config', '${environment.googleAnalyticsToken}', { send_page_view: false });
    `;
    document.head.appendChild(s2);

    // 3) marca como pronto quando o script de rede terminar
    s1.addEventListener('load', () => {
      // pequena proteção: garante que window.gtag existe
      if (typeof window.gtag === 'function') {
        this.ready = true;
        this.flushQueue();
      }
    });
  }

  /** Garante que chamadas só aconteçam após o GA estar pronto */
  private run(fn: () => void) {
    if (this.ready) fn();
    else this.queue.push(fn);
  }

  private flushQueue() {
    for (const fn of this.queue) fn();
    this.queue = [];
  }

  /** Login */
  setUserId(userId: string | number | null | undefined) {
    // evita enviar "undefined" como string
    if (userId === null || userId === undefined || userId === '') {
      this.clearUserId();
      return;
    }
    const id = String(userId);
    this.run(() => {
      window.gtag('set', { user_id: id });
      window.gtag('config', environment.googleAnalyticsToken, { user_id: id });
    });
  }

  /** Logout */
  clearUserId() {
    this.run(() => {
      window.gtag('set', { user_id: undefined });
      window.gtag('config', environment.googleAnalyticsToken, { user_id: undefined });
    });
  }

  /** Pageview manual */
  sendPageView(url: string, title?: string) {
    this.run(() => {
      window.gtag('event', 'page_view', {
        page_location: location.origin + url,
        page_path: url,
        page_title: title ?? document.title
      });
    });
  }

  sendEvent(eventName: string, params?: Record<string, unknown>) {
    this.run(() => window.gtag('event', eventName, params));
  }
}
